import React, { useEffect, useState } from "react";

import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  FaChevronDown,
  FaChevronUp,
  FaArrowDown,
} from "react-icons/fa";

import "./DepositRecord.css";


export default function DepositRecord({
  username: usernameProp,
}) {

  // =====================================================
  // USERNAME
  // =====================================================

  const localUsername =
    localStorage.getItem("username") || "";

  const username =
    usernameProp || localUsername;


  // =====================================================
  // STATE
  // =====================================================

  const [records, setRecords] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [openId, setOpenId] =
    useState(null);


  // =====================================================
  // TIMESTAMP
  // =====================================================

  const getTimestamp = (record) => {

    // Firebase Timestamp - createdAt
    if (
      record?.createdAt?.seconds !== undefined
    ) {

      return (
        Number(
          record.createdAt.seconds
        ) * 1000
      );

    }


    // Firebase Timestamp - timestamp
    if (
      record?.timestamp?.seconds !== undefined
    ) {

      return (
        Number(
          record.timestamp.seconds
        ) * 1000
      );

    }


    // Firebase Timestamp - created_at
    if (
      record?.created_at?.seconds !== undefined
    ) {

      return (
        Number(
          record.created_at.seconds
        ) * 1000
      );

    }


    // Number timestamp - createdAt
    if (
      typeof record?.createdAt ===
      "number"
    ) {

      return Number(
        record.createdAt
      );

    }


    // Number timestamp - timestamp
    if (
      typeof record?.timestamp ===
      "number"
    ) {

      return Number(
        record.timestamp
      );

    }


    // Number timestamp - created_at
    if (
      typeof record?.created_at ===
      "number"
    ) {

      return Number(
        record.created_at
      );

    }


    // String date - createdAt
    if (
      typeof record?.createdAt ===
      "string"
    ) {

      const time =
        new Date(
          record.createdAt
        ).getTime();


      if (
        !Number.isNaN(time)
      ) {

        return time;

      }

    }


    // String date - timestamp
    if (
      typeof record?.timestamp ===
      "string"
    ) {

      const time =
        new Date(
          record.timestamp
        ).getTime();


      if (
        !Number.isNaN(time)
      ) {

        return time;

      }

    }


    // String date - created_at
    if (
      typeof record?.created_at ===
      "string"
    ) {

      const time =
        new Date(
          record.created_at
        ).getTime();


      if (
        !Number.isNaN(time)
      ) {

        return time;

      }

    }


    return 0;

  };


  // =====================================================
  // DATE
  // =====================================================

  const formatDate = (record) => {

    const timestamp =
      getTimestamp(record);


    if (!timestamp) {

      return "Date unavailable";

    }


    const date =
      new Date(timestamp);


    const year =
      date.getFullYear();


    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, "0");


    const day =
      String(
        date.getDate()
      ).padStart(2, "0");


    return `${year}-${month}-${day}`;

  };


  // =====================================================
  // TIME
  // =====================================================

  const formatTime = (record) => {

    const timestamp =
      getTimestamp(record);


    if (!timestamp) {

      return "--:--:--";

    }


    return new Date(
      timestamp
    ).toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }
    );

  };


  // =====================================================
  // AMOUNT
  // =====================================================

  const formatAmount = (amount) => {

    const number =
      Number(amount ?? 0);


    if (
      Number.isNaN(number)
    ) {

      return "0.00";

    }


    return number.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

  };


  // =====================================================
  // STATUS
  // =====================================================

  const getStatus = (record) => {

    if (
      record?.status !==
        undefined &&
      record?.status !==
        null &&
      String(
        record.status
      ).trim() !== ""
    ) {

      return String(
        record.status
      );

    }


    if (
      record?.state !==
        undefined &&
      record?.state !==
        null &&
      String(
        record.state
      ).trim() !== ""
    ) {

      return String(
        record.state
      );

    }


    return "Unknown";

  };


  // =====================================================
  // STATUS CLASS
  // =====================================================

  const getStatusClass = (status) => {

    const value =
      String(
        status || ""
      )
        .toLowerCase()
        .trim();


    if (
      value === "success" ||
      value === "successful" ||
      value === "completed" ||
      value === "complete" ||
      value === "approved" ||
      value === "paid"
    ) {

      return "deposit-status-success";

    }


    if (
      value === "rejected" ||
      value === "failed" ||
      value === "cancelled" ||
      value === "canceled" ||
      value === "declined"
    ) {

      return "deposit-status-failed";

    }


    if (
      value === "pending" ||
      value === "processing" ||
      value === "waiting" ||
      value === "review"
    ) {

      return "deposit-status-pending";

    }


    return "deposit-status-other";

  };


  // =====================================================
  // TYPE
  // =====================================================

  const getType = (record) => {

    // ===================================================
    // USDT TRANSACTIONS
    // ===================================================

    if (
      record?.recordSource ===
      "usdt_transactions"
    ) {

      return "USDT Deposit";

    }


    // ===================================================
    // VOLATILITY FOREX
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityforex"
    ) {

      return "Forex Deposit";

    }


    // ===================================================
    // VOLATILITY OTHER BALANCE
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityotherbalance"
    ) {

      return "Other Balance Deposit";

    }


    // ===================================================
    // EXISTING DEPOSIT TYPES
    // ===================================================

    if (
      record?.depositOrderType ===
      "first"
    ) {

      return "First Deposit";

    }


    if (
      record?.depositOrderType ===
      "second"
    ) {

      return "Second Deposit";

    }


    if (
      record?.depositOrderType ===
      "other"
    ) {

      return "Other Deposit";

    }


    // ===================================================
    // TSH
    // ===================================================

    if (
      record?.recordSource ===
      "tsh_transactions"
    ) {

      return "TSH Deposit";

    }


    // ===================================================
    // VISTERDEPOSTE
    // ===================================================

    if (
      record?.recordSource ===
      "visterdeposte"
    ) {

      return "Deposit";

    }


    // ===================================================
    // CUSTOM TYPE
    // ===================================================

    if (
      record?.type
    ) {

      return String(
        record.type
      );

    }


    return "Deposit";

  };


  // =====================================================
  // TRANSACTION ID
  // =====================================================

  const getTransactionId = (record) => {

    return (
      record?.transactionId ||
      record?.transactionID ||
      record?.transaction_id ||
      record?.txId ||
      record?.txid ||
      record?.reference ||
      record?.ref ||
      "-"
    );

  };


  // =====================================================
  // PAYMENT METHOD
  // =====================================================

  const getPaymentMethod = (record) => {

    // ===================================================
    // USDT
    // ===================================================

    if (
      record?.recordSource ===
      "usdt_transactions"
    ) {

      return (
        record?.method ||
        record?.network ||
        record?.coin ||
        "USDT"
      );

    }


    // ===================================================
    // VOLATILITY FOREX
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityforex"
    ) {

      return (
        record?.paymentMethod ||
        record?.provider ||
        record?.method ||
        record?.depositMethod ||
        "Forex"
      );

    }


    // ===================================================
    // VOLATILITY OTHER BALANCE
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityotherbalance"
    ) {

      return (
        record?.paymentMethod ||
        record?.provider ||
        record?.method ||
        record?.depositMethod ||
        "-"
      );

    }


    // ===================================================
    // OTHER COLLECTIONS
    // ===================================================

    return (
      record?.paymentMethod ||
      record?.provider ||
      record?.method ||
      record?.depositMethod ||
      "-"
    );

  };


  // =====================================================
  // CURRENCY
  // =====================================================

  const getCurrency = (record) => {

    // ===================================================
    // USDT
    // ===================================================

    if (
      record?.recordSource ===
      "usdt_transactions"
    ) {

      return (
        record?.currency ||
        record?.coin ||
        "USDT"
      );

    }


    // ===================================================
    // TSH
    // ===================================================

    if (
      record?.recordSource ===
      "tsh_transactions"
    ) {

      return (
        record?.currency ||
        record?.depositCurrency ||
        "TZS"
      );

    }


    // ===================================================
    // VOLATILITY FOREX
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityforex"
    ) {

      return (
        record?.currency ||
        record?.depositCurrency ||
        record?.depositCurrencyCode ||
        ""
      );

    }


    // ===================================================
    // VOLATILITY OTHER BALANCE
    // ===================================================

    if (
      record?.recordSource ===
      "volatilityotherbalance"
    ) {

      return (
        record?.currency ||
        record?.depositCurrency ||
        "TZS"
      );

    }


    // ===================================================
    // OTHER
    // ===================================================

    return (
      record?.currency ||
      record?.depositCurrency ||
      ""
    );

  };


  // =====================================================
  // DEPOSIT AMOUNT
  // =====================================================

  const getDepositAmount = (record) => {

    if (
      record?.amount !==
      undefined &&
      record?.amount !==
      null
    ) {

      return record.amount;

    }


    if (
      record?.depositAmount !==
      undefined &&
      record?.depositAmount !==
      null
    ) {

      return record.depositAmount;

    }


    return 0;

  };


  // =====================================================
  // RECEIVE AMOUNT
  // =====================================================

  const getReceiveAmount = (record) => {

    if (
      record?.receiveAmount !==
      undefined &&
      record?.receiveAmount !==
      null
    ) {

      return record.receiveAmount;

    }


    if (
      record?.usdtAmount !==
      undefined &&
      record?.usdtAmount !==
      null
    ) {

      return record.usdtAmount;

    }


    return null;

  };


  // =====================================================
  // RECEIVE CURRENCY
  // =====================================================

  const getReceiveCurrency = (record) => {

    return (
      record?.receiveCurrency ||
      record?.depositReceiveCurrency ||
      (
        getReceiveAmount(record) !== null
          ? "USDT"
          : ""
      )
    );

  };


  // =====================================================
  // WALLET ADDRESS
  // =====================================================

  const getWalletAddress = (record) => {

    return (
      record?.address ||
      record?.depositAddress ||
      record?.walletAddress ||
      ""
    );

  };


  // =====================================================
  // RECEIVING ACCOUNT
  // =====================================================

  const getReceivingAccount = (record) => {

    return (
      record?.orbitaPhone ||
      record?.receivingNumber ||
      record?.receivingPhone ||
      record?.customerPhone ||
      record?.phone ||
      ""
    );

  };


  // =====================================================
  // EXCHANGE RATE
  // =====================================================

  const getExchangeRate = (record) => {

    if (
      record?.rate !==
      undefined &&
      record?.rate !==
      null
    ) {

      return record.rate;

    }


    if (
      record?.exchangeRate !==
      undefined &&
      record?.exchangeRate !==
      null
    ) {

      return record.exchangeRate;

    }


    return null;

  };


  // =====================================================
  // TOGGLE DETAILS
  // =====================================================

  const toggleDetails = (id) => {

    setOpenId(
      current =>
        current === id
          ? null
          : id
    );

  };


  // =====================================================
  // LOAD RECORDS
  // =====================================================

  useEffect(() => {

    if (!username) {

      setRecords([]);

      setLoading(false);

      return;

    }


    setLoading(true);


    // ===================================================
    // LOCAL RECORD ARRAYS
    // ===================================================

    let visitorRecords = [];

    let tshRecords = [];

    let usdtRecords = [];

    let volatilityForexRecords = [];

    let volatilityOtherBalanceRecords = [];


    // ===================================================
    // VISTERDEPOSTE QUERY
    // ===================================================

    const visitorQuery =
      query(
        collection(
          db,
          "visterdeposte"
        ),
        where(
          "username",
          "==",
          username
        )
      );


    // ===================================================
    // TSH QUERY
    // ===================================================

    const tshQuery =
      query(
        collection(
          db,
          "tsh_transactions"
        ),
        where(
          "username",
          "==",
          username
        )
      );


    // ===================================================
    // USDT QUERY
    // ===================================================

    const usdtQuery =
      query(
        collection(
          db,
          "usdt_transactions"
        ),
        where(
          "username",
          "==",
          username
        )
      );


    // ===================================================
    // VOLATILITY FOREX QUERY
    // ===================================================

    const volatilityForexQuery =
      query(
        collection(
          db,
          "volatilityforex"
        ),
        where(
          "username",
          "==",
          username
        )
      );


    // ===================================================
    // VOLATILITY OTHER BALANCE QUERY
    // ===================================================

    const volatilityOtherBalanceQuery =
      query(
        collection(
          db,
          "volatilityotherbalance"
        ),
        where(
          "username",
          "==",
          username
        )
      );


    // ===================================================
    // COMBINE RECORDS
    // ===================================================

    const updateRecords = () => {

      const combined = [

        // =============================================
        // VISTERDEPOSTE
        // =============================================

        ...visitorRecords.map(
          item => ({

            ...item,

            recordSource:
              "visterdeposte",

          })
        ),


        // =============================================
        // TSH
        // =============================================

        ...tshRecords.map(
          item => ({

            ...item,

            recordSource:
              "tsh_transactions",

          })
        ),


        // =============================================
        // USDT
        // =============================================

        ...usdtRecords.map(
          item => ({

            ...item,

            recordSource:
              "usdt_transactions",

          })
        ),


        // =============================================
        // VOLATILITY FOREX
        // =============================================

        ...volatilityForexRecords.map(
          item => ({

            ...item,

            recordSource:
              "volatilityforex",

          })
        ),


        // =============================================
        // VOLATILITY OTHER BALANCE
        // =============================================

        ...volatilityOtherBalanceRecords.map(
          item => ({

            ...item,

            recordSource:
              "volatilityotherbalance",

          })
        ),

      ];


      // =================================================
      // NEWEST FIRST
      // =================================================

      combined.sort(
        (a, b) => {

          return (
            getTimestamp(b) -
            getTimestamp(a)
          );

        }
      );


      setRecords(
        combined
      );

      setLoading(false);

    };


    // ===================================================
    // VISTERDEPOSTE SNAPSHOT
    // ===================================================

    const unsubscribeVisitor =
      onSnapshot(

        visitorQuery,

        snapshot => {

          visitorRecords =
            snapshot.docs.map(
              doc => ({

                id:
                  doc.id,

                ...doc.data(),

              })
            );


          updateRecords();

        },

        error => {

          console.error(
            "visterdeposte error:",
            error
          );


          visitorRecords = [];

          updateRecords();

        }

      );


    // ===================================================
    // TSH SNAPSHOT
    // ===================================================

    const unsubscribeTsh =
      onSnapshot(

        tshQuery,

        snapshot => {

          tshRecords =
            snapshot.docs.map(
              doc => ({

                id:
                  doc.id,

                ...doc.data(),

              })
            );


          updateRecords();

        },

        error => {

          console.error(
            "tsh_transactions error:",
            error
          );


          tshRecords = [];

          updateRecords();

        }

      );


    // ===================================================
    // USDT SNAPSHOT
    // ===================================================

    const unsubscribeUsdt =
      onSnapshot(

        usdtQuery,

        snapshot => {

          usdtRecords =
            snapshot.docs.map(
              doc => ({

                id:
                  doc.id,

                ...doc.data(),

              })
            );


          updateRecords();

        },

        error => {

          console.error(
            "usdt_transactions error:",
            error
          );


          usdtRecords = [];

          updateRecords();

        }

      );


    // ===================================================
    // VOLATILITY FOREX SNAPSHOT
    // ===================================================

    const unsubscribeVolatilityForex =
      onSnapshot(

        volatilityForexQuery,

        snapshot => {

          volatilityForexRecords =
            snapshot.docs.map(
              doc => ({

                id:
                  doc.id,

                ...doc.data(),

              })
            );


          updateRecords();

        },

        error => {

          console.error(
            "volatilityforex error:",
            error
          );


          volatilityForexRecords = [];

          updateRecords();

        }

      );


    // ===================================================
    // VOLATILITY OTHER BALANCE SNAPSHOT
    // ===================================================

    const unsubscribeVolatilityOtherBalance =
      onSnapshot(

        volatilityOtherBalanceQuery,

        snapshot => {

          volatilityOtherBalanceRecords =
            snapshot.docs.map(
              doc => ({

                id:
                  doc.id,

                ...doc.data(),

              })
            );


          updateRecords();

        },

        error => {

          console.error(
            "volatilityotherbalance error:",
            error
          );


          volatilityOtherBalanceRecords = [];

          updateRecords();

        }

      );


    // ===================================================
    // CLEANUP
    // ===================================================

    return () => {

      unsubscribeVisitor();

      unsubscribeTsh();

      unsubscribeUsdt();

      unsubscribeVolatilityForex();

      unsubscribeVolatilityOtherBalance();

    };

  }, [username]);


  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (

      <div className="deposit-record">

        <div className="deposit-loading">

          Loading deposit records...

        </div>

      </div>

    );

  }


  // =====================================================
  // USER NOT FOUND
  // =====================================================

  if (!username) {

    return (

      <div className="deposit-record">

        <div className="deposit-empty">

          <div className="deposit-empty-icon">

            <FaArrowDown />

          </div>


          <h3>
            User Not Found
          </h3>


          <p>
            Please log in again to view your deposit records.
          </p>

        </div>

      </div>

    );

  }


  // =====================================================
  // EMPTY
  // =====================================================

  if (
    records.length === 0
  ) {

    return (

      <div className="deposit-record">

        <div className="deposit-empty">

          <div className="deposit-empty-icon">

            <FaArrowDown />

          </div>


          <h3>
            No Deposit Records
          </h3>


          <p>
            Your deposit history will appear here.
          </p>

        </div>

      </div>

    );

  }


  // =====================================================
  // RETURN
  // =====================================================

  return (

    <div className="deposit-record">


      {/* =================================================
          HEADER
      ================================================= */}

      <div className="deposit-record-header">

        <div>

          <h2>
            Deposit Record
          </h2>


          <p>
            Your deposit history
          </p>

        </div>


        <div className="deposit-count">

          {records.length}

        </div>

      </div>


      {/* =================================================
          RECORD LIST
      ================================================= */}

      <div className="deposit-list">

        {records.map(
          record => {

            const uniqueId =
              `${record.recordSource}-${record.id}`;


            const status =
              getStatus(record);


            const isOpen =
              openId === uniqueId;


            const transactionId =
              getTransactionId(
                record
              );


            const isUSDT =
              record.recordSource ===
              "usdt_transactions";


            const currency =
              getCurrency(
                record
              );


            const depositAmount =
              getDepositAmount(
                record
              );


            const receiveAmount =
              getReceiveAmount(
                record
              );


            const receiveCurrency =
              getReceiveCurrency(
                record
              );


            const walletAddress =
              getWalletAddress(
                record
              );


            const receivingAccount =
              getReceivingAccount(
                record
              );


            const exchangeRate =
              getExchangeRate(
                record
              );


            return (

              <div
                key={uniqueId}
                className={`deposit-item ${
                  isOpen
                    ? "is-open"
                    : ""
                }`}
              >


                {/* =======================================
                    MAIN ROW
                ======================================= */}

                <button
                  type="button"
                  className="deposit-main-row"
                  onClick={() =>
                    toggleDetails(
                      uniqueId
                    )
                  }
                >


                  {/* =====================================
                      LEFT SIDE
                  ===================================== */}

                  <div className="deposit-main-left">

                    <div className="deposit-type">

                      {getType(
                        record
                      )}

                    </div>


                    <div className="deposit-date">

                      <span>

                        {formatDate(
                          record
                        )}

                      </span>


                      <span className="deposit-time">

                        {formatTime(
                          record
                        )}

                      </span>

                    </div>

                  </div>


                  {/* =====================================
                      RIGHT SIDE
                  ===================================== */}

                  <div className="deposit-main-right">

                    <div className="deposit-main-amount">

                      {formatAmount(
                        depositAmount
                      )}{" "}

                      {currency}

                    </div>


                    <div
                      className={`deposit-main-status ${getStatusClass(
                        status
                      )}`}
                    >

                      {status}

                    </div>

                  </div>


                  {/* =====================================
                      ARROW
                  ===================================== */}

                  <div className="deposit-arrow">

                    {isOpen ? (

                      <FaChevronUp />

                    ) : (

                      <FaChevronDown />

                    )}

                  </div>

                </button>


                {/* =======================================
                    DETAILS
                ======================================= */}

                {isOpen && (

                  <div className="deposit-details-panel">


                    {/* =================================
                        TYPE
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Type
                      </span>


                      <strong>
                        {getType(
                          record
                        )}
                      </strong>

                    </div>


                    {/* =================================
                        STATUS
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Status
                      </span>


                      <strong
                        className={getStatusClass(
                          status
                        )}
                      >

                        {status}

                      </strong>

                    </div>


                    {/* =================================
                        DATE
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Date
                      </span>


                      <strong>
                        {formatDate(
                          record
                        )}
                      </strong>

                    </div>


                    {/* =================================
                        TIME
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Time
                      </span>


                      <strong>
                        {formatTime(
                          record
                        )}
                      </strong>

                    </div>


                    {/* =================================
                        AMOUNT
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Deposit Amount
                      </span>


                      <strong>

                        {formatAmount(
                          depositAmount
                        )}{" "}

                        {currency}

                      </strong>

                    </div>


                    {/* =================================
                        RECEIVE AMOUNT
                    ================================= */}

                    {receiveAmount !==
                      null && (

                      <div className="detail-row">

                        <span>
                          You Receive
                        </span>


                        <strong>

                          {formatAmount(
                            receiveAmount
                          )}{" "}

                          {receiveCurrency}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        DEPOSIT CURRENCY
                    ================================= */}

                    {record.depositCurrency && (

                      <div className="detail-row">

                        <span>
                          Deposit Currency
                        </span>


                        <strong>

                          {record.depositCurrency}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        COUNTRY
                    ================================= */}

                    {record.country && (

                      <div className="detail-row">

                        <span>
                          Country
                        </span>


                        <strong>

                          {record.flag || ""}{" "}

                          {record.country}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        PAYMENT METHOD
                    ================================= */}

                    <div className="detail-row">

                      <span>
                        Payment Method
                      </span>


                      <strong>

                        {getPaymentMethod(
                          record
                        )}

                      </strong>

                    </div>


                    {/* =================================
                        USDT COIN
                    ================================= */}

                    {isUSDT &&
                      record.coin && (

                      <div className="detail-row">

                        <span>
                          Coin
                        </span>


                        <strong>

                          {record.coin}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        CRYPTO
                    ================================= */}

                    {record.crypto && (

                      <div className="detail-row">

                        <span>
                          Crypto
                        </span>


                        <strong>

                          {record.crypto}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        NETWORK
                    ================================= */}

                    {record.network && (

                      <div className="detail-row">

                        <span>
                          Network
                        </span>


                        <strong>

                          {record.network}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        WALLET TYPE
                    ================================= */}

                    {record.walletType && (

                      <div className="detail-row">

                        <span>
                          Wallet Type
                        </span>


                        <strong>

                          {record.walletType}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        WALLET ADDRESS
                    ================================= */}

                    {walletAddress && (

                      <div className="detail-row">

                        <span>
                          Wallet Address
                        </span>


                        <strong
                          className="wallet-address"
                        >

                          {walletAddress}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        RECEIVING ACCOUNT
                    ================================= */}

                    {receivingAccount && (

                      <div className="detail-row">

                        <span>
                          Receiving Account
                        </span>


                        <strong>

                          {receivingAccount}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        RECEIVING NUMBER
                    ================================= */}

                    {record.receivingNumber && (

                      <div className="detail-row">

                        <span>
                          Receiving Number
                        </span>


                        <strong>

                          {record.receivingNumber}

                        </strong>

                      </div>

                    )}

                    {/* =================================
                        EXCHANGE RATE
                    ================================= */}

                    {exchangeRate !==
                      null && (

                      <div className="detail-row">

                        <span>
                          Exchange Rate
                        </span>


                        <strong>

                          {formatAmount(
                            exchangeRate
                          )}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        MINIMUM DEPOSIT
                    ================================= */}

                    {record.minimumAmount !==
                      undefined && (

                      <div className="detail-row">

                        <span>
                          Minimum Deposit
                        </span>


                        <strong>

                          {formatAmount(
                            record.minimumAmount
                          )}{" "}

                          {currency}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        BUSINESS NUMBER
                    ================================= */}

                    {record.businessNumber && (

                      <div className="detail-row">

                        <span>
                          Business Number
                        </span>


                        <strong>

                          {record.businessNumber}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        CUSTOMER PHONE
                    ================================= */}

{record.customerPhone && (

<div className="detail-row">

  <span>
    Customer Phone
  </span>


  <strong>

    {record.customerPhone}

  </strong>

</div>

)}


                    {/* =================================
                        DEPOSIT METHOD
                    ================================= */}

                    {record.depositMethod && (

                      <div className="detail-row">

                        <span>
                          Deposit Method
                        </span>


                        <strong>

                          {record.depositMethod}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        DEPOSIT ADDRESS
                    ================================= */}

                    {record.depositAddress &&
                      record.depositAddress !==
                      walletAddress && (

                      <div className="detail-row">

                        <span>
                          Deposit Address
                        </span>


                        <strong
                          className="wallet-address"
                        >

                          {record.depositAddress}

                        </strong>

                      </div>

                    )}


                    {/* =================================
                        TRANSACTION ID
                    ================================= */}

                    {transactionId !== "-" && (

                      <div className="detail-row">

                        <span>
                          Transaction ID
                        </span>


                        <strong
                          className="transaction-id"
                        >

                          {transactionId}

                        </strong>

                      </div>

                    )}

                  </div>

                )}

              </div>

            );

          }
        )}

      </div>

    </div>

  );

}